import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Paper, Typography, Card, CardContent,
  CircularProgress, Button, Chip, Divider
} from '@mui/material';
import {
  ShoppingCart as OrderIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
  <Card
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: 4
      } : {}
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={color || 'text.primary'}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: `${color || 'primary'}.lighter`,
          color: color || 'primary.main'
        }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_orders: 0,
    pending_orders: 0,
    paid_orders: 0,
    shipping_orders: 0,
    delivered_orders: 0,
    total_revenue: 0,
    today_orders: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/orders?limit=5')
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.stats);
      }
      if (ordersRes.data.success) {
        setRecentOrders(ordersRes.data.orders);
      }
    } catch (error) {
      toast.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    pending: 'warning',
    paid: 'info',
    shipping: 'primary',
    delivered: 'success',
    cancelled: 'error'
  };

  const statusLabels = {
    pending: '待付款',
    paid: '已付款',
    shipping: '出貨中',
    delivered: '已送達',
    cancelled: '已取消'
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            儀表板
          </Typography>
          <Typography variant="body2" color="text.secondary">
            歡迎回來！這是您的商店概況。
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>
          重新整理
        </Button>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="今日訂單"
            value={stats.today_orders}
            icon={<TrendingIcon />}
            color="primary"
            subtitle="新訂單數量"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="待處理"
            value={`${stats.pending_orders + stats.paid_orders} 件`}
            icon={<PendingIcon />}
            color="warning"
            subtitle={`待付款 ${stats.pending_orders} ・ 待出貨 ${stats.paid_orders}`}
            onClick={() => navigate('/admin/orders')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="出貨中"
            value={stats.shipping_orders}
            icon={<ShippingIcon />}
            color="info"
            subtitle="等待送達"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="總營收"
            value={`$${(stats.total_revenue || 0).toLocaleString()}`}
            icon={<MoneyIcon />}
            color="success"
            subtitle={`共 ${stats.total_orders} 筆訂單`}
          />
        </Grid>
      </Grid>

      {/* Quick Actions & Recent Orders */}
      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              快速操作
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<OrderIcon />}
                onClick={() => navigate('/admin/orders')}
                sx={{ justifyContent: 'flex-start' }}
              >
                查看所有訂單
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<TrendingIcon />}
                onClick={() => navigate('/admin/analytics')}
                sx={{ justifyContent: 'flex-start' }}
              >
                數據分析報表
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ShippingIcon />}
                onClick={() => navigate('/admin/orders?status=paid')}
                sx={{ justifyContent: 'flex-start' }}
              >
                待出貨訂單
              </Button>
              <Divider sx={{ my: 1 }} />
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/admin/settings')}
                sx={{ justifyContent: 'flex-start' }}
              >
                系統設定
              </Button>
            </Box>
          </Paper>

          {/* Order Status Summary */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              訂單狀態統計
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { label: '待付款', value: stats.pending_orders, color: 'warning' },
                { label: '已付款', value: stats.paid_orders, color: 'info' },
                { label: '出貨中', value: stats.shipping_orders, color: 'primary' },
                { label: '已送達', value: stats.delivered_orders, color: 'success' }
              ].map(item => (
                <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">{item.label}</Typography>
                  <Chip
                    label={item.value}
                    size="small"
                    color={item.color}
                    sx={{ minWidth: 50 }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Orders */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                最新訂單
              </Typography>
              <Button size="small" onClick={() => navigate('/admin/orders')}>
                查看全部
              </Button>
            </Box>

            {recentOrders.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>
                暫無訂單
              </Typography>
            ) : (
              <Box>
                {recentOrders.map((order, index) => (
                  <Box key={order.order_no}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1,
                        px: 1
                      }}
                      onClick={() => navigate(`/admin/orders?search=${order.order_no}`)}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {order.order_no}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.customer_name} - {order.customer_phone}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(order.created_at).toLocaleString('zh-TW')}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          ${order.total_amount}
                        </Typography>
                        <Chip
                          label={statusLabels[order.status] || order.status}
                          size="small"
                          color={statusColors[order.status] || 'default'}
                        />
                      </Box>
                    </Box>
                    {index < recentOrders.length - 1 && <Divider />}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
